import * as Sentry from '@sentry/browser';
import {isEqual, once} from 'lodash';
import {useEffect, useLayoutEffect, useReducer, useRef} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {useIntl, type IntlShape} from 'react-intl';
import {graphql} from 'react-relay';
import {createOperationDescriptor, fetchQuery, getRequest, type Disposable, type Snapshot} from 'relay-runtime';
import environment from '../../../../RelayEnvironment';
import AcceptAgenticWorkerResultMutation from '../../../../mutations/agentic-workers/AcceptAgenticWorkerResultMutation';
import DeleteAgenticWorkerResultMutation from '../../../../mutations/agentic-workers/DeleteAgenticWorkerResultMutation';
import HideAgenticWorkerResultMutation from '../../../../mutations/agentic-workers/HideAgenticWorkerResultMutation';
import StartAgenticWorkerRunMutation from '../../../../mutations/agentic-workers/StartAgenticWorkerRunMutation';
import StopAgenticWorkerRunMutation from '../../../../mutations/agentic-workers/StopAgenticWorkerRunMutation';
import type {AcceptAgenticWorkerResultMutation$data} from '../../../../mutations/agentic-workers/__generated__/AcceptAgenticWorkerResultMutation.graphql';
import type {DeleteAgenticWorkerResultMutation$data} from '../../../../mutations/agentic-workers/__generated__/DeleteAgenticWorkerResultMutation.graphql';
import type {
	AgenticWorkerRowType,
	StartAgenticWorkerRunMutation$data,
} from '../../../../mutations/agentic-workers/__generated__/StartAgenticWorkerRunMutation.graphql';
import type {StopAgenticWorkerRunMutation$data} from '../../../../mutations/agentic-workers/__generated__/StopAgenticWorkerRunMutation.graphql';
import Util from '../../util/util';
import {createToast} from '../toasts/toast';
import type {agentRunStatePastRunsQuery} from './__generated__/agentRunStatePastRunsQuery.graphql';
import type {agentRunStateQuery} from './__generated__/agentRunStateQuery.graphql';
import type {agentRunStateScheduleQuery} from './__generated__/agentRunStateScheduleQuery.graphql';
import type {agentRunStateSettleQuery} from './__generated__/agentRunStateSettleQuery.graphql';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import {
	AGENT_RESULT_STATE,
	AGENT_RUN_SCOPE,
	AGENT_RUN_STATE,
	AGENT_RUN_STATE_REASON,
	type AgentDeclaration,
	type AgentLastRun,
	type AgentPastResult,
	type AgentPastRun,
	type AgentPastRunCounts,
	type AgentRecord,
	type AgentResult,
	type AgentResultState,
	type AgentRow,
	type AgentRun,
	type AgentRunOutcome,
	type AgentRunScope,
	type AgentRunState,
	type AgentRunStateReason,
	type AgentStartedBy,
} from './agentDeclaration';

export const POLL_INTERVAL_MILLISECONDS = 3000;
export const SETTLE_READ_ATTEMPTS = 3;
export const SCHEDULE_READ_ATTEMPTS = 3;
const TOAST_DURATION_MILLISECONDS = 5000;
// The server fails a run that is still going after this long.
const RUN_TIME_LIMIT_MINUTES = 20;
const VISIBILITY_CHANGE_EVENT = 'visibilitychange';
const VISIBLE_STATE: DocumentVisibilityState = 'visible';
const REVIEWED_MARKS_STORAGE_PREFIX = 'agentic_workers.reviewed';

export const PAGE_RUN_SCOPE: AgentRunScope = {kind: AGENT_RUN_SCOPE.PAGE};

export const AGENT_ROW_STATUS = {
	IDLE: 'IDLE',
	WORKING: 'WORKING',
	READY: 'READY',
} as const;
export type AgentRowStatus = (typeof AGENT_ROW_STATUS)[keyof typeof AGENT_ROW_STATUS];

export const PAST_RUNS_PAGE_SIZE = 50;
// The most runs one past runs read answers, as service-company caps it. Older runs are found by searching.
export const PAST_RUNS_MAX_LIMIT = 500;
export const PAST_RUNS_RETRY_MAX_MILLISECONDS = 60 * 1000;

export const PAST_RUN_FILTER = {
	ALL: 'ALL',
	NEEDS_REVIEW: 'NEEDS_REVIEW',
	DONE: 'DONE',
	DELETED: 'DELETED',
} as const;
export type PastRunFilter = (typeof PAST_RUN_FILTER)[keyof typeof PAST_RUN_FILTER];

export type AgentPastRunsQuery = {
	// As typed. The read sends it trimmed, and none when it is blank.
	search: string;
	filter: PastRunFilter;
};

export type AgentPastRuns = {
	runs: AgentPastRun[];
	hasMore: boolean;
	counts: AgentPastRunCounts;
	// What this answer was read for.
	limit: number;
	query: AgentPastRunsQuery;
};

const NO_PAST_RUNS_QUERY: AgentPastRunsQuery = {
	search: '',
	filter: PAST_RUN_FILTER.ALL,
};

const NO_PAST_RUN_COUNTS: AgentPastRunCounts = {
	all: 0,
	needsReview: 0,
	done: 0,
	deleted: 0,
};

export type AgentRowState = {
	status: AgentRowStatus;
	result: AgentResult | null;
	// The result an idle row was reviewed up to on an earlier visit, while it still waits.
	reviewedResult: AgentResult | null;
	inPageRun: boolean;
	// True while this row's own accept, or an accept of its whole result, waits for its answer.
	isAccepting: boolean;
};

export type AgentRunSnapshot = {
	activeRuns: AgentRun[];
	// The run that finished last first.
	waitingResults: AgentResult[];
	// By row key: the result each row was reviewed up to on an earlier visit. A row reviewed during this visit stays
	// Ready until the visit ends.
	reviewedRows: ReadonlyMap<string, string>;
	lastRun: AgentLastRun | null;
	// True from a run leaving the active runs until a read sent after that answers, because until then the last run can
	// still be the one before it.
	lastRunPending: boolean;
	// False until a run read answers, with or without a worker, and true from then on.
	firstReadAnswered: boolean;
	// Whether the agent's schedule set is off or has no schedules. Null until the set is in the Relay store, and kept as it
	// was when a later read fails.
	scheduleOff: boolean | null;
	pendingStartKeys: ReadonlySet<string>;
	heldStopKeys: ReadonlySet<string>;
	stoppingRunIds: ReadonlySet<string>;
	restartAfterStopKeys: ReadonlySet<string>;
	// The accepts and Deletes sent and not yet answered, keyed as their commands are: a whole result by its id, one row by
	// its result id and row.
	acceptingKeys: ReadonlySet<string>;
	deletingKeys: ReadonlySet<string>;
	// Null until a read answers while a drawer watches the past runs, and again once nothing watches them.
	pastRuns: AgentPastRuns | null;
	// True from Show more until the latest read answers, or a new search or chip starts again from the first page.
	isReadingMorePastRuns: boolean;
};

type AgentRunListener = () => void;

type StopListener = () => void;

type RunFinishedListener = (outcome: AgentRunOutcome, scope: AgentRunScope) => void;

// A run again is watched to its end like a page run, so the finish listeners hear how it ended.
type AgentStartOptions = {announceRunAgain: boolean};

// Kept with its options, so a run again asked for during a stop is still announced when it starts.
type AgentRestart = {
	scope: AgentRunScope;
	options?: AgentStartOptions;
};

// A produced outcome whose results the settle read did not list, kept with the scope of its run.
type HeldOutcome = {
	outcome: AgentRunOutcome;
	scope: AgentRunScope;
};

type ActedFirstListener = (actedByName: string | null, state: AgentResultState | null) => void;

export type AgentDeleteListeners = {
	onDeleted: () => void;
	onActedFirst: ActedFirstListener;
};

type AgentAcceptListeners = {
	onRefused: (reason: string) => void;
	onActedFirst: ActedFirstListener;
};

// What an accept or Delete settles: one row, which leaves every waiting result, or the whole result. An accept of a
// whole result takes its rows out of every other waiting result too; a Delete of a whole result leaves them there.
type SettledChange = {
	resultId: string;
	rows: AgentRow[];
	whole: boolean;
};

type AgentScheduleSet = {
	readonly enabled: boolean;
	readonly schedules: ReadonlyArray<unknown>;
};

const STOP_ANSWER = {
	STOPPED: 'STOPPED',
	FINISHED_FIRST: 'FINISHED_FIRST',
	FAILED: 'FAILED',
} as const;
type StopAnswer = (typeof STOP_ANSWER)[keyof typeof STOP_ANSWER];

type AgentRunEntry = {
	snapshot: AgentRunSnapshot;
	listeners: Set<AgentRunListener>;
	pendingStarts: Set<string>;
	// By scope key: a start sent as a run again and not yet answered.
	announcedStartKeys: Set<string>;
	// By scope key: a Stop clicked before its start answered.
	heldStops: Map<string, StopListener>;
	// By run id: a stop sent and not yet answered.
	stopsInFlight: Map<string, StopListener>;
	// By scope key: a Run clicked while that scope's stop was held or in flight, sent once a sent stop settles.
	restartsAfterStop: Map<string, AgentRestart>;
	// Result commands sent and not yet answered, each kept with the change it settles as the snapshot held it when sent.
	// A Delete, by result id or by result id and row, applies to the snapshot at once and over every read until its
	// answer. So does a Hide, by result id, which settles nothing and leaves its change unused. An accept, keyed as a
	// Delete is, changes nothing until the server says it applied.
	deletesInFlight: Map<string, SettledChange>;
	hidesInFlight: Map<string, SettledChange>;
	acceptsInFlight: Map<string, SettledChange>;
	isReading: boolean;
	// Set when the store asks for a read because its snapshot is not read yet or out of date, so polling carries on
	// until a read succeeds.
	needsFreshRead: boolean;
	// Polling retries a failing read every few seconds, so a run of failures is reported once.
	hasReportedFailedRead: boolean;
	pollTimer: ReturnType<typeof setTimeout> | null;
	// A read sent before the latest answered start, stop, accept, Delete or Hide can be stale, so its answer is thrown
	// away.
	answeredChanges: number;
	// graphql reads the last run alongside the active runs, so a read sent before a run left can answer the run before
	// it as the last run. Counts the runs that left, and the count when the latest applied read was sent.
	runEnds: number;
	runEndsWhenReadSent: number;
	// The page runs this tab has seen running, and the runs again it started. Only these are settled when they leave the
	// active runs.
	watchedRunIds: Set<string>;
	// A watched run that has left the active runs. The next read asks how it ended.
	settlingRunId: string | null;
	failedSettleReads: number;
	// By run id: told on the next successful read.
	heldOutcomes: Map<string, HeldOutcome>;
	finishListeners: Set<RunFinishedListener>;
	// The schedule set is read apart from the runs, and followed in the Relay store, only while something watches it.
	scheduleConsumer: string | null;
	scheduleWatchers: number;
	scheduleFollow: Disposable | null;
	isReadingSchedule: boolean;
	scheduleRetryTimer: ReturnType<typeof setTimeout> | null;
	// Null until a read names the viewer, whose reviewed marks are stored apart from other people's.
	personId: string | null;
	// Replaced rather than changed in place, because the snapshot shares it as its reviewed rows.
	earlierVisitMarks: ReadonlyMap<string, string>;
	marksThisVisit: Map<string, string>;
	// The address the visit began on. Controls that unmount and mount again there, behind a loader, keep the visit.
	visitPathname: string | null;
	// The past runs are read only while a drawer watches them, for the search, chip and limit it asked for.
	pastRunsWatchers: number;
	pastRunsQuery: AgentPastRunsQuery;
	pastRunsLimit: number;
	// Several reads can be out at once, and only the latest one sent applies, so an answer for an earlier search, chip or
	// limit is dropped.
	pastRunsReadsSent: number;
	isReadingMorePastRuns: boolean;
	// Failed past runs reads in a row: the first is reported, and each waits longer before the next try.
	failedPastRunsReads: number;
	pastRunsRetryTimer: ReturnType<typeof setTimeout> | null;
};

type RowNode = {
	readonly rowType: string;
	readonly rowId: number;
};

type ProjectNode = {
	readonly companyProjectId: number | null;
	readonly customProjectId: string | null;
	readonly name: string | null;
	readonly projectColor: string | null;
};

type ResultRowNode = RowNode & {
	readonly project: ProjectNode | null;
};

type PersonNode = {
	readonly fullName: string | null;
};

type RunNode = {
	readonly id: string;
	readonly scopeKind: string;
	readonly scopeRowType?: string | null;
	readonly scopeRowId?: number | null;
	readonly state: string;
	readonly rows?: ReadonlyArray<RowNode> | null;
};

type SettledRunNode = RunNode & {
	readonly stateReason: string | null;
	readonly foundDetail: string | null;
	readonly finishedAt: string | null;
};

type ResultNode = {
	readonly id: string;
	readonly runId: string;
	readonly rows: ReadonlyArray<ResultRowNode>;
	readonly state: string;
	readonly hidden: boolean;
	readonly producedAt: string | null;
};

type ReadResponse = {
	readonly viewer: {
		readonly actualPersonId: string | null;
		readonly agenticWorker: {
			readonly activeRuns: ReadonlyArray<RunNode>;
			readonly waitingResults: ReadonlyArray<ResultNode>;
			readonly lastRun: SettledRunNode | null;
			readonly run?: SettledRunNode | null;
		} | null;
	} | null;
};

type PastRunNode = SettledRunNode & {
	readonly startedByKind: string;
	readonly startedBy: PersonNode | null;
};

type PastResultNode = {
	readonly resultId: string;
	readonly state: string;
	readonly rows: ReadonlyArray<ResultRowNode>;
	readonly actedBy: PersonNode | null;
	readonly actedAt: string | null;
	readonly hidden: boolean;
	readonly producedAt: string | null;
};

type PastRunEntryNode = {
	readonly run: PastRunNode;
	readonly results: ReadonlyArray<PastResultNode>;
};

type PastRunsResponse = {
	readonly viewer: {
		readonly agenticWorker: {
			readonly pastRuns: {
				readonly runs: ReadonlyArray<PastRunEntryNode>;
				readonly hasMore: boolean;
				readonly counts: Readonly<AgentPastRunCounts>;
			};
		} | null;
	} | null;
};

// Spread unmasked, so the reads get these fields as plain data and nothing reads the fragments themselves.
graphql`
	fragment agentRunState_run on AgenticWorkerRun {
		id
		scopeKind
		scopeRowType
		scopeRowId
		state
		rows {
			rowType
			rowId
		}
	}
`;

graphql`
	fragment agentRunState_worker on AgenticWorker {
		activeRuns {
			...agentRunState_run @relay(mask: false)
		}
		lastRun {
			...agentRunState_run @relay(mask: false)
			stateReason
			foundDetail
			finishedAt
		}
		waitingResults {
			id
			runId
			rows {
				rowType
				rowId
				project {
					companyProjectId
					customProjectId
					name
					projectColor
				}
			}
			state
			hidden
			producedAt
		}
	}
`;

const query = graphql`
	query agentRunStateQuery($agentId: String!) {
		viewer {
			actualPersonId
			agenticWorker(agentId: $agentId) {
				...agentRunState_worker @relay(mask: false)
			}
		}
	}
`;

const settleQuery = graphql`
	query agentRunStateSettleQuery($agentId: String!, $runId: ID!) {
		viewer {
			actualPersonId
			agenticWorker(agentId: $agentId) {
				...agentRunState_worker @relay(mask: false)
				run(id: $runId) {
					...agentRunState_run @relay(mask: false)
					stateReason
					foundDetail
					finishedAt
				}
			}
		}
	}
`;

// Apart from the run reads: graphql answers an error whenever scheduling fails or is not set up, and an error fails the
// whole read it is part of.
const scheduleQuery = graphql`
	query agentRunStateScheduleQuery($consumer: String!) {
		viewer {
			scheduleSet(consumer: $consumer) {
				id
				enabled
				schedules {
					id
				}
			}
		}
	}
`;

// Apart from the run reads, and only while a drawer watches it, with the search, chip and limit that drawer asked for.
const pastRunsReadQuery = graphql`
	query agentRunStatePastRunsQuery($agentId: String!, $limit: Int!, $search: String, $state: AgenticWorkerResultState) {
		viewer {
			agenticWorker(agentId: $agentId) {
				pastRuns(limit: $limit, search: $search, state: $state) {
					hasMore
					counts {
						all
						needsReview
						done
						deleted
					}
					runs {
						run {
							...agentRunState_run @relay(mask: false)
							startedByKind
							startedBy {
								fullName
							}
							stateReason
							foundDetail
							finishedAt
						}
						results {
							resultId
							state
							rows {
								rowType
								rowId
								project {
									companyProjectId
									customProjectId
									name
									projectColor
								}
							}
							actedBy {
								fullName
							}
							actedAt
							hidden
							producedAt
						}
					}
				}
			}
		}
	}
`;

const EMPTY_SNAPSHOT: AgentRunSnapshot = {
	activeRuns: [],
	waitingResults: [],
	reviewedRows: new Map(),
	lastRun: null,
	lastRunPending: false,
	firstReadAnswered: false,
	scheduleOff: null,
	pendingStartKeys: new Set(),
	heldStopKeys: new Set(),
	stoppingRunIds: new Set(),
	restartAfterStopKeys: new Set(),
	acceptingKeys: new Set(),
	deletingKeys: new Set(),
	pastRuns: null,
	isReadingMorePastRuns: false,
};

const entries = new Map<string, AgentRunEntry>();

const entryFor = (agentId: string): AgentRunEntry => {
	const existing = entries.get(agentId);
	if (existing) {
		return existing;
	}
	const created: AgentRunEntry = {
		snapshot: EMPTY_SNAPSHOT,
		listeners: new Set(),
		pendingStarts: new Set(),
		announcedStartKeys: new Set(),
		heldStops: new Map(),
		stopsInFlight: new Map(),
		restartsAfterStop: new Map(),
		deletesInFlight: new Map(),
		hidesInFlight: new Map(),
		acceptsInFlight: new Map(),
		isReading: false,
		needsFreshRead: false,
		hasReportedFailedRead: false,
		pollTimer: null,
		answeredChanges: 0,
		runEnds: 0,
		runEndsWhenReadSent: 0,
		watchedRunIds: new Set(),
		settlingRunId: null,
		failedSettleReads: 0,
		heldOutcomes: new Map(),
		finishListeners: new Set(),
		scheduleConsumer: null,
		scheduleWatchers: 0,
		scheduleFollow: null,
		isReadingSchedule: false,
		scheduleRetryTimer: null,
		personId: null,
		earlierVisitMarks: new Map(),
		marksThisVisit: new Map(),
		visitPathname: null,
		pastRunsWatchers: 0,
		pastRunsQuery: NO_PAST_RUNS_QUERY,
		pastRunsLimit: PAST_RUNS_PAGE_SIZE,
		pastRunsReadsSent: 0,
		isReadingMorePastRuns: false,
		failedPastRunsReads: 0,
		pastRunsRetryTimer: null,
	};
	entries.set(agentId, created);
	return created;
};

const toRowScope = (row: AgentRow): AgentRunScope => ({kind: AGENT_RUN_SCOPE.ROW, ...row});

export const toRowKey = (row: AgentRow): string => `${row.rowType}#${row.rowId}`;

const coversRow =
	(row: AgentRow) =>
	(candidate: AgentRow): boolean =>
		candidate.rowType === row.rowType && candidate.rowId === row.rowId;

// A command on one row is kept apart from the same command on its result's other rows and on the whole result, so two
// rows can be deleted or accepted at once.
const toResultCommandKey = (resultId: string, row: AgentRow | null): string =>
	row === null ? resultId : `${resultId}#${toRowKey(row)}`;

export const toScopeKey = (scope: AgentRunScope): string =>
	scope.kind === AGENT_RUN_SCOPE.PAGE ? AGENT_RUN_SCOPE.PAGE : `${AGENT_RUN_SCOPE.ROW}#${toRowKey(scope)}`;

export const reviewedMarksStorageKey = (personId: string, agentId: string): string =>
	`${REVIEWED_MARKS_STORAGE_PREFIX}.${personId}.${agentId}`;

const isStoredMark = (mark: [string, unknown]): mark is [string, string] => typeof mark[1] === 'string';

const readStoredMarks = (storageKey: string): Map<string, string> => {
	try {
		const stored: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '{}');
		return typeof stored === 'object' && stored !== null ? new Map(Object.entries(stored).filter(isStoredMark)) : new Map();
	} catch {
		return new Map();
	}
};

const writeStoredMarks = (storageKey: string, marks: ReadonlyMap<string, string>): void => {
	try {
		if (marks.size === 0) {
			localStorage.removeItem(storageKey);
		} else {
			localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(marks)));
		}
	} catch {
		// The marks only decide whether a row reads Ready or idle, so storage that is blocked or full leaves them in this
		// tab's memory.
	}
};

const mergeMarks = (olderMarks: ReadonlyMap<string, string>, newerMarks: ReadonlyMap<string, string>): Map<string, string> =>
	new Map([...olderMarks, ...newerMarks]);

const toAgentRow = (node: RowNode): AgentRow => ({
	rowType: node.rowType,
	rowId: node.rowId,
});

const toAgentResultRow = (node: ResultRowNode): AgentRow => ({
	...toAgentRow(node),
	project: node.project
		? {
				companyProjectId: node.project.companyProjectId,
				customProjectId: node.project.customProjectId,
				name: node.project.name,
				color: node.project.projectColor,
		  }
		: null,
});

// A row run that lost its row would read as a page run, so it is dropped.
const toAgentRun = (node: RunNode): AgentRun | null => {
	const state = node.state as AgentRunState;
	const rows = (node.rows ?? []).map(toAgentRow);
	if (node.scopeKind !== AGENT_RUN_SCOPE.ROW) {
		return {
			id: node.id,
			scope: PAGE_RUN_SCOPE,
			state,
			rows,
		};
	}
	if (!node.scopeRowType || typeof node.scopeRowId !== 'number') {
		return null;
	}
	return {
		id: node.id,
		scope: {
			kind: AGENT_RUN_SCOPE.ROW,
			rowType: node.scopeRowType,
			rowId: node.scopeRowId,
		},
		state,
		rows,
	};
};

const isPresent = <Value>(value: Value | null): value is Value => value !== null;

const toAgentResult = (node: ResultNode): AgentResult => ({
	id: node.id,
	runId: node.runId,
	rows: node.rows.map(toAgentResultRow),
	state: node.state as AgentResultState,
	hidden: node.hidden,
	producedAt: node.producedAt,
});

// The detail is JSON the agent owns, so a detail that does not parse is left out rather than losing the summary.
const parseFoundDetail = (foundDetail: string | null): unknown => {
	if (foundDetail === null) {
		return null;
	}
	try {
		return JSON.parse(foundDetail);
	} catch {
		return null;
	}
};

const toAgentLastRun = (node: SettledRunNode): AgentLastRun | null => {
	const run = toAgentRun(node);
	if (run === null) {
		return null;
	}
	return {
		id: run.id,
		scope: run.scope,
		state: run.state,
		stateReason: node.stateReason as AgentRunStateReason | null,
		detail: parseFoundDetail(node.foundDetail),
		finishedAt: node.finishedAt,
	};
};

const toAgentPastResult =
	(runId: string) =>
	(node: PastResultNode): AgentPastResult => ({
		id: node.resultId,
		runId,
		rows: node.rows.map(toAgentResultRow),
		state: node.state as AgentResultState,
		hidden: node.hidden,
		producedAt: node.producedAt,
		actedByName: node.actedBy?.fullName ?? null,
		actedAt: node.actedAt,
	});

// A run whose row is gone is left out, as the last run is.
const toAgentPastRun = ({run, results}: PastRunEntryNode): AgentPastRun | null => {
	const settledRun = toAgentLastRun(run);
	if (settledRun === null) {
		return null;
	}
	return {
		...settledRun,
		startedBy: run.startedByKind as AgentStartedBy,
		startedByName: run.startedBy?.fullName ?? null,
		results: results.map(toAgentPastResult(settledRun.id)),
	};
};

// The server answers no worker while the feature is off for this viewer, so no run of it has ended.
const toAgentPastRuns = (response: PastRunsResponse | undefined, limit: number, query: AgentPastRunsQuery): AgentPastRuns => {
	const pastRuns = response?.viewer?.agenticWorker?.pastRuns;
	return {
		runs: pastRuns?.runs.map(toAgentPastRun).filter(isPresent) ?? [],
		hasMore: pastRuns?.hasMore ?? false,
		counts: pastRuns
			? {
					all: pastRuns.counts.all,
					needsReview: pastRuns.counts.needsReview,
					done: pastRuns.counts.done,
					deleted: pastRuns.counts.deleted,
			  }
			: NO_PAST_RUN_COUNTS,
		limit,
		query,
	};
};

const toPastRunsVariables = (agentId: string, limit: number, query: AgentPastRunsQuery) => {
	const search = query.search.trim();
	return {
		agentId,
		limit,
		search: search === '' ? null : search,
		state: query.filter === PAST_RUN_FILTER.ALL ? null : query.filter,
	};
};

const toScopeInput = (scope: AgentRunScope) =>
	scope.kind === AGENT_RUN_SCOPE.PAGE
		? {scopeKind: AGENT_RUN_SCOPE.PAGE}
		: {
				scopeKind: AGENT_RUN_SCOPE.ROW,
				// Host pages name their row types; the records accept the values the server's enum lists.
				scopeRowType: scope.rowType as AgenticWorkerRowType,
				scopeRowId: scope.rowId,
		  };

const needsPolling = (entry: AgentRunEntry): boolean =>
	entry.needsFreshRead ||
	entry.settlingRunId !== null ||
	entry.snapshot.activeRuns.length > 0 ||
	entry.pendingStarts.size > 0 ||
	entry.stopsInFlight.size > 0;

const cancelPoll = (entry: AgentRunEntry): void => {
	if (entry.pollTimer !== null) {
		clearTimeout(entry.pollTimer);
		entry.pollTimer = null;
	}
};

const schedulePoll = (agentId: string, entry: AgentRunEntry): void => {
	if (entry.pollTimer !== null || entry.isReading || entry.listeners.size === 0 || !needsPolling(entry)) {
		return;
	}
	entry.pollTimer = setTimeout(() => {
		entry.pollTimer = null;
		// schedulePoll and readAgentRuns call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		readAgentRuns(agentId);
	}, POLL_INTERVAL_MILLISECONDS);
};

type AgentRunRead = Pick<AgentRunSnapshot, 'activeRuns' | 'waitingResults' | 'lastRun'>;

type AgentRunChanges = Partial<AgentRunRead & Pick<AgentRunSnapshot, 'firstReadAnswered' | 'scheduleOff' | 'pastRuns'>>;

const isLastRunPending = (entry: AgentRunEntry): boolean => entry.runEndsWhenReadSent !== entry.runEnds;

const toSettledChange = (
	snapshot: AgentRunSnapshot,
	resultId: string,
	row: AgentRow | null,
	clearsOtherResults: boolean
): SettledChange => ({
	resultId,
	rows: !clearsOtherResults
		? []
		: row === null
		? snapshot.waitingResults.find(result => result.id === resultId)?.rows ?? []
		: [row],
	whole: row === null,
});

// A result the change leaves with no rows is settled too.
const applySettledChange = (results: AgentResult[], change: SettledChange): AgentResult[] =>
	results.flatMap(result => {
		if (change.whole && result.id === change.resultId) {
			return [];
		}
		const remainingRows = result.rows.filter(resultRow => !change.rows.some(coversRow(resultRow)));
		if (remainingRows.length === result.rows.length) {
			return [result];
		}
		return remainingRows.length > 0 ? [{...result, rows: remainingRows}] : [];
	});

const withResultCommandsInFlight = (entry: AgentRunEntry, waitingResults: AgentResult[]): AgentResult[] =>
	Array.from(entry.deletesInFlight.values())
		.reduce(applySettledChange, waitingResults)
		.map(result => (entry.hidesInFlight.has(result.id) && !result.hidden ? {...result, hidden: true} : result));

// What a part still in review reads once a whole accept or Delete settles it, until the past runs are read again.
type SettledPastPart = Pick<AgentPastResult, 'state' | 'actedByName' | 'actedAt'>;

// A Delete's answer cannot say who acted, because part of the result may have been accepted earlier by someone else.
const DELETED_PAST_PART: SettledPastPart = {
	state: AGENT_RESULT_STATE.DELETED,
	actedByName: null,
	actedAt: null,
};

// A whole result's accept or Delete acts on its rows still in review, so only its Needs Review parts change. A run with
// none of them is kept as it was, and so is the list when no run changes, so what the drawer shows keeps its identity
// from one render to the next.
const settlePastRunParts = (
	runs: AgentPastRun[],
	isSettled: (resultId: string) => boolean,
	settledPart: SettledPastPart
): AgentPastRun[] => {
	const settledRuns = runs.map(run => {
		const isPartSettled = (part: AgentPastResult): boolean =>
			part.state === AGENT_RESULT_STATE.NEEDS_REVIEW && isSettled(part.id);
		if (!run.results.some(isPartSettled)) {
			return run;
		}
		return {
			...run,
			results: run.results.map(part => (isPartSettled(part) ? {...part, ...settledPart} : part)),
		};
	});
	return settledRuns.some((settledRun, runIndex) => settledRun !== runs[runIndex]) ? settledRuns : runs;
};

const publish = (agentId: string, entry: AgentRunEntry, changes: AgentRunChanges = {}): void => {
	entry.snapshot = {
		...entry.snapshot,
		...changes,
		waitingResults: withResultCommandsInFlight(entry, changes.waitingResults ?? entry.snapshot.waitingResults),
		reviewedRows: entry.earlierVisitMarks,
		lastRunPending: isLastRunPending(entry),
		pendingStartKeys: new Set(entry.pendingStarts),
		heldStopKeys: new Set(entry.heldStops.keys()),
		stoppingRunIds: new Set(entry.stopsInFlight.keys()),
		restartAfterStopKeys: new Set(entry.restartsAfterStop.keys()),
		acceptingKeys: new Set(entry.acceptsInFlight.keys()),
		deletingKeys: new Set(entry.deletesInFlight.keys()),
		isReadingMorePastRuns: entry.isReadingMorePastRuns,
	};
	// Outside a React event, React 16 renders each listener's update on its own, so they are batched into one render.
	unstable_batchedUpdates(() => {
		entry.listeners.forEach(listener => listener());
	});
	schedulePoll(agentId, entry);
};

const cancelPastRunsRetry = (entry: AgentRunEntry): void => {
	if (entry.pastRunsRetryTimer !== null) {
		clearTimeout(entry.pastRunsRetryTimer);
		entry.pastRunsRetryTimer = null;
	}
};

// A failed past runs read is tried again after 3 seconds, then twice as long after each failure in a row, up to a minute.
const pastRunsRetryDelay = (failedReads: number): number =>
	Math.min(POLL_INTERVAL_MILLISECONDS * 2 ** (failedReads - 1), PAST_RUNS_RETRY_MAX_MILLISECONDS);

// Read only while watched, never by a poll. An answer for a closed drawer, or for a read that is no longer the latest, is
// dropped. A read sent before the latest answered command is thrown away and sent again, so a row never goes back to
// what it was before an accept or Delete. A failed read keeps what shows and is tried again, less often the longer it
// keeps failing and not while the tab is out of view, which reads again when it comes back. A run of failures is
// reported once.
const readAgentPastRuns = (agentId: string, entry: AgentRunEntry): void => {
	if (entry.pastRunsWatchers === 0) {
		return;
	}
	cancelPastRunsRetry(entry);
	entry.pastRunsReadsSent += 1;
	const readNumber = entry.pastRunsReadsSent;
	const changesWhenSent = entry.answeredChanges;
	const limit = entry.pastRunsLimit;
	const query = entry.pastRunsQuery;
	const isLatestWatchedRead = (): boolean =>
		entries.get(agentId) === entry && entry.pastRunsWatchers > 0 && entry.pastRunsReadsSent === readNumber;
	fetchQuery<agentRunStatePastRunsQuery>(
		environment.getInstance(),
		pastRunsReadQuery,
		toPastRunsVariables(agentId, limit, query)
	)
		.toPromise()
		.then(
			response => {
				if (!isLatestWatchedRead()) {
					return;
				}
				entry.failedPastRunsReads = 0;
				if (entry.answeredChanges !== changesWhenSent) {
					readAgentPastRuns(agentId, entry);
					return;
				}
				const pastRuns = toAgentPastRuns(response, limit, query);
				if (isEqual(entry.snapshot.pastRuns, pastRuns) && !entry.isReadingMorePastRuns) {
					return;
				}
				entry.isReadingMorePastRuns = false;
				publish(agentId, entry, {pastRuns});
			},
			() => {
				if (!isLatestWatchedRead()) {
					return;
				}
				entry.failedPastRunsReads += 1;
				if (entry.failedPastRunsReads === 1) {
					Sentry.captureMessage(
						`Reading the past runs of agent "${agentId}" failed; keeping the past runs shown`,
						'warning'
					);
				}
				// The raised limit was not read, so Show more is offered again for the runs that show.
				if (entry.isReadingMorePastRuns) {
					entry.isReadingMorePastRuns = false;
					entry.pastRunsLimit = entry.snapshot.pastRuns?.limit ?? PAST_RUNS_PAGE_SIZE;
					publish(agentId, entry);
				}
				entry.pastRunsRetryTimer = setTimeout(() => {
					entry.pastRunsRetryTimer = null;
					if (document.visibilityState === VISIBLE_STATE) {
						readAgentPastRuns(agentId, entry);
					}
				}, pastRunsRetryDelay(entry.failedPastRunsReads));
			}
		);
};

const applyRead = (agentId: string, entry: AgentRunEntry, read: AgentRunRead): void => {
	const waitingResults = withResultCommandsInFlight(entry, read.waitingResults);
	const isUnchanged =
		isEqual(entry.snapshot.activeRuns, read.activeRuns) &&
		isEqual(entry.snapshot.waitingResults, waitingResults) &&
		isEqual(entry.snapshot.lastRun, read.lastRun) &&
		entry.snapshot.reviewedRows === entry.earlierVisitMarks &&
		entry.snapshot.lastRunPending === isLastRunPending(entry) &&
		entry.snapshot.firstReadAnswered;
	if (isUnchanged) {
		return;
	}
	const hasLastRunChanged = !isEqual(entry.snapshot.lastRun, read.lastRun);
	publish(agentId, entry, {
		...read,
		waitingResults,
		firstReadAnswered: true,
	});
	// A run has ended since, so the past runs list it now.
	if (hasLastRunChanged) {
		readAgentPastRuns(agentId, entry);
	}
};

const storeReviewedMarks = (agentId: string, entry: AgentRunEntry): void => {
	if (entry.personId !== null) {
		writeStoredMarks(
			reviewedMarksStorageKey(entry.personId, agentId),
			mergeMarks(entry.earlierVisitMarks, entry.marksThisVisit)
		);
	}
};

const loadReviewedMarks = (agentId: string, entry: AgentRunEntry, personId: string | null): void => {
	if (entry.personId !== null || personId === null) {
		return;
	}
	entry.personId = personId;
	entry.earlierVisitMarks = mergeMarks(readStoredMarks(reviewedMarksStorageKey(personId, agentId)), entry.earlierVisitMarks);
};

// Replaced rather than changed in place, because the snapshot shares the earlier visit's marks as its reviewed rows.
const keepMarks = (agentId: string, entry: AgentRunEntry, isKept: (rowKey: string, resultId: string) => boolean): void => {
	const earlierVisitMarks = new Map([...entry.earlierVisitMarks].filter(([rowKey, resultId]) => isKept(rowKey, resultId)));
	const marksThisVisit = new Map([...entry.marksThisVisit].filter(([rowKey, resultId]) => isKept(rowKey, resultId)));
	const hasDroppedEarlierMark = earlierVisitMarks.size < entry.earlierVisitMarks.size;
	if (!hasDroppedEarlierMark && marksThisVisit.size === entry.marksThisVisit.size) {
		return;
	}
	if (hasDroppedEarlierMark) {
		entry.earlierVisitMarks = earlierVisitMarks;
	}
	entry.marksThisVisit = marksThisVisit;
	storeReviewedMarks(agentId, entry);
};

// An accept or Delete the server applied takes the marks of the rows it settled with it, and every mark of a whole
// result, since a read that lists no results cannot tell they are gone.
const dropMarksOfSettledChange = (agentId: string, entry: AgentRunEntry, change: SettledChange): void => {
	const settledRowKeys = new Set(change.rows.map(toRowKey));
	keepMarks(
		agentId,
		entry,
		(rowKey, resultId) => !settledRowKeys.has(rowKey) && !(change.whole && resultId === change.resultId)
	);
};

// graphql answers no waiting results when its read of them fails, so only a read that lists some tells which are gone.
const dropMarksOfResultsGone = (agentId: string, entry: AgentRunEntry, waitingResults: AgentResult[]): void => {
	if (waitingResults.length === 0) {
		return;
	}
	const waitingResultIds = new Set(waitingResults.map(result => result.id));
	keepMarks(agentId, entry, (rowKey, resultId) => waitingResultIds.has(resultId));
};

const endVisit = (agentId: string, entry: AgentRunEntry): void => {
	entry.visitPathname = null;
	if (entry.marksThisVisit.size > 0) {
		entry.earlierVisitMarks = mergeMarks(entry.earlierVisitMarks, entry.marksThisVisit);
		entry.marksThisVisit = new Map();
		publish(agentId, entry);
	}
};

// The last control can unmount while the address still shows the visit's page, so the first control to mount again
// checks the address too.
const startVisit = (agentId: string, entry: AgentRunEntry): void => {
	if (entry.visitPathname !== null && entry.visitPathname !== window.location.pathname) {
		endVisit(agentId, entry);
	}
	if (entry.visitPathname === null) {
		entry.visitPathname = window.location.pathname;
	}
};

const hasRunLeft = (activeRunsBefore: AgentRun[], activeRunsAfter: AgentRun[]): boolean =>
	activeRunsBefore.some(run => !activeRunsAfter.some(active => active.id === run.id));

// With no control subscribed, the read waits for the next control to subscribe, which reads at once.
const requestFreshRead = (agentId: string, entry: AgentRunEntry): void => {
	entry.needsFreshRead = true;
	if (entry.listeners.size > 0) {
		// requestFreshRead and readAgentRuns call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		readAgentRuns(agentId);
	}
};

const clearSettling = (entry: AgentRunEntry): void => {
	entry.settlingRunId = null;
	entry.failedSettleReads = 0;
};

const endWatch = (entry: AgentRunEntry, runId: string): void => {
	entry.watchedRunIds.delete(runId);
	if (entry.settlingRunId === runId) {
		clearSettling(entry);
	}
};

// Forgotten when the last control unsubscribes, so a control that subscribes later never tells of a run it did not
// show ending.
const forgetWatchedRuns = (entry: AgentRunEntry): void => {
	entry.watchedRunIds.clear();
	entry.heldOutcomes.clear();
	clearSettling(entry);
};

const watchRun = (entry: AgentRunEntry, runId: string): void => {
	if (entry.listeners.size > 0) {
		entry.watchedRunIds.add(runId);
	}
};

const watchPageRuns = (entry: AgentRunEntry): void => {
	entry.snapshot.activeRuns.filter(run => run.scope.kind === AGENT_RUN_SCOPE.PAGE).forEach(run => watchRun(entry, run.id));
};

const settleNextEndedRun = (agentId: string, entry: AgentRunEntry): void => {
	const endedRunId = Array.from(entry.watchedRunIds).find(runId => !entry.snapshot.activeRuns.some(run => run.id === runId));
	if (endedRunId === undefined) {
		return;
	}
	entry.settlingRunId = endedRunId;
	requestFreshRead(agentId, entry);
};

const notifyRunFinished = (entry: AgentRunEntry, outcome: AgentRunOutcome, scope: AgentRunScope): void => {
	entry.finishListeners.forEach(listener => {
		// Listeners run agent-owned code, and one that throws must not stop the store reading.
		try {
			listener(outcome, scope);
		} catch (error) {
			Sentry.captureException(error);
		}
	});
};

// Told once, with whatever the read lists for the run: a run whose results are gone by now is told with none.
const notifyHeldOutcomes = (entry: AgentRunEntry, waitingResults: AgentResult[]): void => {
	const held = Array.from(entry.heldOutcomes);
	entry.heldOutcomes.clear();
	held.forEach(([runId, {outcome, scope}]) =>
		notifyRunFinished(
			entry,
			{
				...outcome,
				results: waitingResults.filter(result => result.runId === runId),
			},
			scope
		)
	);
};

const settleRun = (
	agentId: string,
	entry: AgentRunEntry,
	runId: string,
	run: SettledRunNode | null,
	waitingResults: AgentResult[]
): void => {
	if (run?.state === AGENT_RUN_STATE.RUNNING) {
		clearSettling(entry);
		// graphql answers no active runs when its read of them fails, so a run that is still going is put back.
		const runningRun = toAgentRun(run);
		if (runningRun && !entry.snapshot.activeRuns.some(active => active.id === runId)) {
			publish(agentId, entry, {activeRuns: [...entry.snapshot.activeRuns, runningRun]});
		}
		return;
	}
	endWatch(entry, runId);
	const settledRun = run === null ? null : toAgentLastRun(run);
	if (settledRun?.state !== AGENT_RUN_STATE.PRODUCED) {
		return;
	}
	// graphql answers no waiting results when its read of them fails, so one more read makes sure the run's results
	// show.
	entry.needsFreshRead = true;
	const outcome: AgentRunOutcome = {
		state: AGENT_RUN_STATE.PRODUCED,
		stateReason: settledRun.stateReason,
		results: waitingResults.filter(result => result.runId === runId),
		detail: settledRun.detail,
	};
	if (outcome.results.length === 0) {
		// A toast must not report that failed read as finding none, so the trailing read tells it with the results it
		// lists for the run.
		entry.heldOutcomes.set(runId, {
			outcome,
			scope: settledRun.scope,
		});
		return;
	}
	notifyRunFinished(entry, outcome, settledRun.scope);
};

const failSettleRead = (agentId: string, entry: AgentRunEntry, runId: string): void => {
	entry.failedSettleReads += 1;
	if (entry.failedSettleReads < SETTLE_READ_ATTEMPTS) {
		return;
	}
	Sentry.captureMessage(`Settling run "${runId}" of agent "${agentId}" failed; no toast will show`, 'warning');
	endWatch(entry, runId);
};

// A module-level store shared by many controls cannot use the useForecastFetchQuery hook, and this read is
// supplementary, not the page's loading query.
const fetchAgentRuns = (agentId: string, settlingRunId: string | null): Promise<ReadResponse | undefined> =>
	settlingRunId === null
		? fetchQuery<agentRunStateQuery>(environment.getInstance(), query, {agentId}).toPromise()
		: fetchQuery<agentRunStateSettleQuery>(environment.getInstance(), settleQuery, {
				agentId,
				runId: settlingRunId,
		  }).toPromise();

function readAgentRuns(agentId: string): void {
	const entry = entryFor(agentId);
	if (entry.isReading) {
		return;
	}
	cancelPoll(entry);
	entry.isReading = true;
	const changesWhenSent = entry.answeredChanges;
	const runEndsWhenSent = entry.runEnds;
	fetchAgentRuns(agentId, entry.settlingRunId).then(
		response => {
			if (entries.get(agentId) !== entry) {
				return;
			}
			entry.isReading = false;
			entry.hasReportedFailedRead = false;
			if (entry.answeredChanges !== changesWhenSent) {
				requestFreshRead(agentId, entry);
				return;
			}
			entry.needsFreshRead = false;
			// The server answers no worker while the feature is off for this viewer, so nothing runs or waits.
			const worker = response?.viewer?.agenticWorker;
			const activeRuns = worker?.activeRuns.map(toAgentRun).filter(isPresent) ?? [];
			const waitingResults = worker?.waitingResults.map(toAgentResult) ?? [];
			loadReviewedMarks(agentId, entry, response?.viewer?.actualPersonId ?? null);
			dropMarksOfResultsGone(agentId, entry, waitingResults);
			if (worker && hasRunLeft(entry.snapshot.activeRuns, activeRuns)) {
				entry.runEnds += 1;
				entry.needsFreshRead = true;
			}
			entry.runEndsWhenReadSent = runEndsWhenSent;
			applyRead(agentId, entry, {
				activeRuns,
				waitingResults,
				lastRun: worker?.lastRun ? toAgentLastRun(worker.lastRun) : null,
			});
			watchPageRuns(entry);
			notifyHeldOutcomes(entry, waitingResults);
			if (entry.settlingRunId !== null) {
				settleRun(agentId, entry, entry.settlingRunId, worker?.run ?? null, waitingResults);
			}
			settleNextEndedRun(agentId, entry);
			schedulePoll(agentId, entry);
		},
		() => {
			if (entries.get(agentId) !== entry) {
				return;
			}
			entry.isReading = false;
			if (entry.settlingRunId !== null) {
				failSettleRead(agentId, entry, entry.settlingRunId);
			} else if (!entry.hasReportedFailedRead) {
				entry.hasReportedFailedRead = true;
				Sentry.captureMessage(`Reading the runs of agent "${agentId}" failed; keeping the last known runs`, 'warning');
			}
			schedulePoll(agentId, entry);
		}
	);
}

const isScheduleOff = (set: AgentScheduleSet): boolean => !set.enabled || set.schedules.length === 0;

// Follows the set in the Relay store, where the Schedule drawer writes it too, rather than taking each read's answer.
// So the line moves with a write as it is made and with its rollback, even once the drawer has closed, and Relay keeps
// a write still in flight over a read that answers before it lands.
const followAgentSchedule = (agentId: string, entry: AgentRunEntry, consumer: string): Disposable => {
	const relayEnvironment = environment.getInstance();
	const operation = createOperationDescriptor(getRequest(scheduleQuery), {consumer});
	const retention = relayEnvironment.retain(operation);
	const applySnapshot = ({data, isMissingData}: Snapshot): void => {
		const set = (data as agentRunStateScheduleQuery['response'] | null)?.viewer?.scheduleSet;
		if (isMissingData || !set) {
			return;
		}
		const scheduleOff = isScheduleOff(set);
		if (entry.snapshot.scheduleOff !== scheduleOff) {
			publish(agentId, entry, {scheduleOff});
		}
	};
	const snapshot = relayEnvironment.lookup(operation.fragment);
	applySnapshot(snapshot);
	const subscription = relayEnvironment.subscribe(snapshot, applySnapshot);
	return {
		dispose: () => {
			subscription.dispose();
			retention.dispose();
		},
	};
};

const cancelScheduleRetry = (entry: AgentRunEntry): void => {
	if (entry.scheduleRetryTimer !== null) {
		clearTimeout(entry.scheduleRetryTimer);
		entry.scheduleRetryTimer = null;
	}
};

const stopFollowingAgentSchedule = (entry: AgentRunEntry): void => {
	entry.scheduleFollow?.dispose();
	entry.scheduleFollow = null;
	cancelScheduleRetry(entry);
};

// The answer reaches the line through the Relay store. graphql answers an error whenever scheduling fails, so a failed
// read keeps the last known state and is tried again a few times while the line watches. One read at a time, so an
// older answer never lands after a newer one.
const readAgentSchedule = (agentId: string, entry: AgentRunEntry, attempt = 1): void => {
	if (entry.isReadingSchedule || entry.scheduleConsumer === null) {
		return;
	}
	cancelScheduleRetry(entry);
	entry.isReadingSchedule = true;
	fetchQuery<agentRunStateScheduleQuery>(environment.getInstance(), scheduleQuery, {consumer: entry.scheduleConsumer})
		.toPromise()
		.then(
			() => {
				entry.isReadingSchedule = false;
			},
			() => {
				entry.isReadingSchedule = false;
				if (entries.get(agentId) !== entry || entry.scheduleWatchers === 0 || attempt >= SCHEDULE_READ_ATTEMPTS) {
					return;
				}
				entry.scheduleRetryTimer = setTimeout(() => {
					entry.scheduleRetryTimer = null;
					readAgentSchedule(agentId, entry, attempt + 1);
				}, POLL_INTERVAL_MILLISECONDS);
			}
		);
};

const findActiveRun = (entry: AgentRunEntry, scopeKey: string): AgentRun | undefined =>
	entry.snapshot.activeRuns.find(run => toScopeKey(run.scope) === scopeKey);

const isStopUnderway = (entry: AgentRunEntry, scopeKey: string): boolean => {
	if (entry.pendingStarts.has(scopeKey)) {
		return entry.heldStops.has(scopeKey);
	}
	const run = findActiveRun(entry, scopeKey);
	return run !== undefined && entry.stopsInFlight.has(run.id);
};

type StandardErrorFlow = (error: Error) => boolean | void;

const finishStop = (agentId: string, run: AgentRun, answer: StopAnswer): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(run.scope);
	const onStopped = entry.stopsInFlight.get(run.id);
	entry.stopsInFlight.delete(run.id);
	// Any other answer keeps the run watched, so a stop that lost the race to the finish still settles it.
	if (answer === STOP_ANSWER.STOPPED) {
		endWatch(entry, run.id);
	}
	if (answer === STOP_ANSWER.FAILED) {
		// The person has just seen the standard error modal, and starting again would most likely show a second one.
		entry.restartsAfterStop.delete(scopeKey);
	}
	entry.answeredChanges += 1;
	// Whatever the answer, the run leaves the snapshot here, and one more read tells whether it still runs and which run
	// was the last.
	entry.runEnds += 1;
	publish(agentId, entry, {activeRuns: entry.snapshot.activeRuns.filter(active => active.id !== run.id)});
	requestFreshRead(agentId, entry);
	const restart = entry.restartsAfterStop.get(scopeKey);
	if (restart) {
		// Removed only now, so the control reads Working throughout instead of flickering idle.
		entry.restartsAfterStop.delete(scopeKey);
		// A start can hold a stop, and a stop can hold a start, so the two call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		startAgentRun(agentId, restart.scope, restart.options);
	}
	const isStartingAgain = entry.pendingStarts.has(scopeKey) || entry.restartsAfterStop.has(scopeKey);
	if (restart && !isStartingAgain) {
		// The start found another run going for the scope and sent nothing, so the waiting Run leaves the snapshot here.
		publish(agentId, entry);
	}
	// While the scope starts again at once, "Run stopped" would contradict the person's last click.
	if (answer === STOP_ANSWER.STOPPED && !isStartingAgain) {
		onStopped?.();
	}
};

const sendStop = (agentId: string, run: AgentRun, onStopped: StopListener): void => {
	const entry = entryFor(agentId);
	entry.stopsInFlight.set(run.id, onStopped);
	publish(agentId, entry);
	Util.CommitMutation(
		StopAgenticWorkerRunMutation,
		{id: run.id},
		(response: StopAgenticWorkerRunMutation$data) => {
			finishStop(
				agentId,
				run,
				response?.stopAgenticWorkerRun?.run?.state === AGENT_RUN_STATE.STOPPED
					? STOP_ANSWER.STOPPED
					: STOP_ANSWER.FINISHED_FIRST
			);
		},
		true,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finishStop(agentId, run, STOP_ANSWER.FAILED);
		}
	);
};

const finishStart = (agentId: string, scopeKey: string, run: AgentRun | null): void => {
	const entry = entryFor(agentId);
	const heldStop = entry.heldStops.get(scopeKey);
	const isRunAgain = entry.announcedStartKeys.has(scopeKey);
	entry.pendingStarts.delete(scopeKey);
	entry.announcedStartKeys.delete(scopeKey);
	entry.heldStops.delete(scopeKey);
	entry.answeredChanges += 1;
	if (run && run.state === AGENT_RUN_STATE.RUNNING) {
		if (run.scope.kind === AGENT_RUN_SCOPE.PAGE || isRunAgain) {
			watchRun(entry, run.id);
		}
		publish(agentId, entry, {activeRuns: [...entry.snapshot.activeRuns.filter(active => active.id !== run.id), run]});
		if (heldStop) {
			sendStop(agentId, run, heldStop);
		}
		return;
	}
	// A held Stop that is never sent drops the Run waiting on it: after NOT_STARTED a run is already going for the
	// scope, and after a failure sending it again would only fail again.
	entry.restartsAfterStop.delete(scopeKey);
	// The start ended without a run of its own, so it counts as a run ending: until one more read tells what runs and
	// which run was the last, no failed card shows the failure from before it.
	entry.runEnds += 1;
	publish(agentId, entry);
	requestFreshRead(agentId, entry);
};

export const getAgentRunSnapshot = (agentId: string): AgentRunSnapshot => entries.get(agentId)?.snapshot ?? EMPTY_SNAPSHOT;

export const subscribeToRunFinished = (agentId: string, listener: RunFinishedListener): (() => void) => {
	const entry = entryFor(agentId);
	entry.finishListeners.add(listener);
	return () => {
		entry.finishListeners.delete(listener);
	};
};

const isAnyAgentSubscribed = (): boolean => Array.from(entries.values()).some(entry => entry.listeners.size > 0);

// Nothing polls while idle, so what others did while the tab was out of view shows when it comes back.
const readSubscribedAgentsOnReturn = (): void => {
	if (document.visibilityState !== VISIBLE_STATE) {
		return;
	}
	entries.forEach((entry, agentId) => {
		if (entry.listeners.size > 0) {
			requestFreshRead(agentId, entry);
		}
		if (entry.scheduleWatchers > 0) {
			readAgentSchedule(agentId, entry);
		}
		readAgentPastRuns(agentId, entry);
	});
};

export const subscribeToAgentRun = (agentId: string, listener: AgentRunListener): (() => void) => {
	const entry = entryFor(agentId);
	if (!isAnyAgentSubscribed()) {
		document.addEventListener(VISIBILITY_CHANGE_EVENT, readSubscribedAgentsOnReturn);
	}
	const isFirstListener = entry.listeners.size === 0;
	// Added first, so the listener hears the reviewed rows change when startVisit ends an earlier visit.
	entry.listeners.add(listener);
	if (isFirstListener) {
		startVisit(agentId, entry);
		requestFreshRead(agentId, entry);
	}
	return () => {
		entry.listeners.delete(listener);
		if (entry.listeners.size === 0) {
			cancelPoll(entry);
			forgetWatchedRuns(entry);
			if (entry.visitPathname !== window.location.pathname) {
				endVisit(agentId, entry);
			}
		}
		if (!isAnyAgentSubscribed()) {
			document.removeEventListener(VISIBILITY_CHANGE_EVENT, readSubscribedAgentsOnReturn);
		}
	};
};

// Read when the first watcher arrives and again on return to the tab, never by a run poll: a run does not change the
// schedule.
export const watchAgentSchedule = (agentId: string, consumer: string): (() => void) => {
	const entry = entryFor(agentId);
	entry.scheduleConsumer = consumer;
	entry.scheduleWatchers += 1;
	if (entry.scheduleWatchers === 1) {
		entry.scheduleFollow = followAgentSchedule(agentId, entry, consumer);
		readAgentSchedule(agentId, entry);
	}
	return () => {
		entry.scheduleWatchers -= 1;
		if (entry.scheduleWatchers === 0) {
			stopFollowingAgentSchedule(entry);
		}
	};
};

// The next read starts again from the first page with no search and All. A read in flight is no longer the latest, so
// its answer is dropped.
const forgetPastRuns = (agentId: string, entry: AgentRunEntry): void => {
	cancelPastRunsRetry(entry);
	entry.pastRunsReadsSent += 1;
	entry.pastRunsQuery = NO_PAST_RUNS_QUERY;
	entry.pastRunsLimit = PAST_RUNS_PAGE_SIZE;
	entry.isReadingMorePastRuns = false;
	entry.failedPastRunsReads = 0;
	publish(agentId, entry, {pastRuns: null});
};

// Read when the first watcher arrives, and again on the store's own events: an accept or Delete answering, a run ending
// and a return to the tab. The last watcher to leave drops what was read.
export const watchAgentPastRuns = (agentId: string): (() => void) => {
	const entry = entryFor(agentId);
	entry.pastRunsWatchers += 1;
	if (entry.pastRunsWatchers === 1) {
		readAgentPastRuns(agentId, entry);
	}
	return () => {
		entry.pastRunsWatchers -= 1;
		if (entry.pastRunsWatchers === 0) {
			forgetPastRuns(agentId, entry);
		}
	};
};

// While a new search or chip is read, what shows stays until its answer replaces it.
export const setAgentPastRunsQuery = (agentId: string, query: AgentPastRunsQuery): void => {
	const entry = entryFor(agentId);
	if (entry.pastRunsWatchers === 0) {
		return;
	}
	entry.pastRunsQuery = {...query};
	entry.pastRunsLimit = PAST_RUNS_PAGE_SIZE;
	if (entry.isReadingMorePastRuns) {
		entry.isReadingMorePastRuns = false;
		publish(agentId, entry);
	}
	readAgentPastRuns(agentId, entry);
};

// Every later read asks for the raised limit too, so new runs come in at the top and nothing loaded moves.
export const showMoreAgentPastRuns = (agentId: string): void => {
	const entry = entryFor(agentId);
	if (entry.pastRunsWatchers === 0 || entry.pastRunsLimit >= PAST_RUNS_MAX_LIMIT) {
		return;
	}
	entry.pastRunsLimit = Math.min(entry.pastRunsLimit + PAST_RUNS_PAGE_SIZE, PAST_RUNS_MAX_LIMIT);
	entry.isReadingMorePastRuns = true;
	publish(agentId, entry);
	readAgentPastRuns(agentId, entry);
};

export const startAgentRun = (agentId: string, scope: AgentRunScope, options?: AgentStartOptions): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(scope);
	if (isStopUnderway(entry, scopeKey)) {
		if (!entry.restartsAfterStop.has(scopeKey)) {
			entry.restartsAfterStop.set(scopeKey, {
				scope,
				options,
			});
			publish(agentId, entry);
		}
		return;
	}
	if (entry.pendingStarts.has(scopeKey) || findActiveRun(entry, scopeKey)) {
		return;
	}
	if (options?.announceRunAgain) {
		entry.announcedStartKeys.add(scopeKey);
	}
	entry.pendingStarts.add(scopeKey);
	publish(agentId, entry);
	Util.CommitMutation(
		StartAgenticWorkerRunMutation,
		{agentId, ...toScopeInput(scope)},
		(response: StartAgenticWorkerRunMutation$data) => {
			const node = response?.startAgenticWorkerRun?.run;
			finishStart(agentId, scopeKey, node ? toAgentRun(node) : null);
		},
		true,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finishStart(agentId, scopeKey, null);
		}
	);
};

export const stopAgentRun = (agentId: string, scope: AgentRunScope, onStopped: StopListener): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(scope);
	if (entry.restartsAfterStop.has(scopeKey)) {
		entry.restartsAfterStop.delete(scopeKey);
		publish(agentId, entry);
		return;
	}
	if (entry.pendingStarts.has(scopeKey)) {
		entry.heldStops.set(scopeKey, onStopped);
		publish(agentId, entry);
		return;
	}
	const run = findActiveRun(entry, scopeKey);
	if (run && !entry.stopsInFlight.has(run.id)) {
		sendStop(agentId, run, onStopped);
	}
};

type ResultCommand<MutationResponse> = {
	inFlightOf: (entry: AgentRunEntry) => Map<string, SettledChange>;
	key: string;
	mutation: unknown;
	retry: boolean;
	clearsOtherResults: boolean;
	// Whether its answer can change the past runs, which are then read again.
	changesPastRuns: boolean;
	// Whether the answer says the server made the change, which then applies to the snapshot with the answer.
	isApplied?: (response: MutationResponse) => boolean;
	// What a whole result's parts still in review read in the past runs after this answer, until they are read again.
	// Null when the answer did not settle them.
	settledPastPart?: (response: MutationResponse) => SettledPastPart | null;
	onAnswer: (response: MutationResponse) => void;
};

const toResultCommandInput = (resultId: string, row: AgentRow | null) =>
	row === null
		? {id: resultId}
		: {
				id: resultId,
				rowType: row.rowType as AgenticWorkerRowType,
				rowId: row.rowId,
		  };

const sendResultCommand = <MutationResponse>(
	agentId: string,
	resultId: string,
	row: AgentRow | null,
	command: ResultCommand<MutationResponse>
): void => {
	const entry = entryFor(agentId);
	const inFlight = command.inFlightOf(entry);
	if (inFlight.has(command.key)) {
		return;
	}
	const change = toSettledChange(entry.snapshot, resultId, row, command.clearsOtherResults);
	inFlight.set(command.key, change);
	publish(agentId, entry);
	// Whatever the answer, one more read tells what waits now. The command leaves the snapshot in the same publish as the
	// change it applied, so no control sees an accept end before its rows go.
	const finish = (changes: AgentRunChanges = {}): void => {
		inFlight.delete(command.key);
		entry.answeredChanges += 1;
		publish(agentId, entry, changes);
		requestFreshRead(agentId, entry);
		if (command.changesPastRuns) {
			readAgentPastRuns(agentId, entry);
		}
	};
	const toAnsweredChanges = (response: MutationResponse, isApplied: boolean): AgentRunChanges => {
		const {waitingResults, pastRuns} = entry.snapshot;
		// A row's accept or Delete splits its part, so only the read that follows can show it.
		const settledPastPart = row === null ? command.settledPastPart?.(response) ?? null : null;
		const isThisResult = (partResultId: string): boolean => partResultId === resultId;
		return {
			waitingResults: isApplied ? applySettledChange(waitingResults, change) : waitingResults,
			pastRuns:
				pastRuns === null || settledPastPart === null
					? pastRuns
					: {
							...pastRuns,
							runs: settlePastRunParts(pastRuns.runs, isThisResult, settledPastPart),
					  },
		};
	};
	Util.CommitMutation(
		command.mutation,
		toResultCommandInput(resultId, row),
		(response: MutationResponse) => {
			const isApplied = command.isApplied?.(response) === true;
			if (isApplied) {
				dropMarksOfSettledChange(agentId, entry, change);
			}
			finish(toAnsweredChanges(response, isApplied));
			command.onAnswer(response);
		},
		command.retry,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finish();
		}
	);
};

type ActedOnResult = {
	readonly state: string;
	readonly actedBy: PersonNode | null;
};

const tellActedFirst = (onActedFirst: ActedFirstListener, result: ActedOnResult | null | undefined): void =>
	onActedFirst(result?.actedBy?.fullName ?? null, (result?.state as AgentResultState | undefined) ?? null);

export const acceptAgentResult = (
	agentId: string,
	resultId: string,
	row: AgentRow | null,
	listeners: AgentAcceptListeners
): void =>
	sendResultCommand(agentId, resultId, row, {
		inFlightOf: entry => entry.acceptsInFlight,
		key: toResultCommandKey(resultId, row),
		mutation: AcceptAgenticWorkerResultMutation,
		// An accept whose answer was lost and is sent again answers that it did not apply, as if someone else had acted
		// first.
		retry: false,
		clearsOtherResults: true,
		changesPastRuns: true,
		isApplied: (response: AcceptAgenticWorkerResultMutation$data) => response?.acceptAgenticWorkerResult?.accepted === true,
		// The person who pressed accept is the last to act on the result, so the answer says who and when.
		settledPastPart: (response: AcceptAgenticWorkerResultMutation$data) => {
			const answer = response?.acceptAgenticWorkerResult;
			if (!answer?.accepted) {
				return null;
			}
			return {
				state: AGENT_RESULT_STATE.DONE,
				actedByName: answer.result?.actedBy?.fullName ?? null,
				actedAt: answer.result?.actedAt ?? null,
			};
		},
		onAnswer: (response: AcceptAgenticWorkerResultMutation$data) => {
			const answer = response?.acceptAgenticWorkerResult;
			if (answer?.accepted) {
				return;
			}
			const refusalReason = answer?.refusalReason ?? null;
			if (refusalReason !== null) {
				listeners.onRefused(refusalReason);
				return;
			}
			tellActedFirst(listeners.onActedFirst, answer?.result);
		},
	});

export const deleteAgentResult = (
	agentId: string,
	resultId: string,
	row: AgentRow | null,
	listeners: AgentDeleteListeners
): void =>
	sendResultCommand(agentId, resultId, row, {
		inFlightOf: entry => entry.deletesInFlight,
		key: toResultCommandKey(resultId, row),
		mutation: DeleteAgenticWorkerResultMutation,
		// A Delete whose answer was lost and is sent again answers that nothing was deleted, as if someone else had
		// acted first.
		retry: false,
		// Deleting a whole result keeps a newer result someone asked for that includes one of its rows.
		clearsOtherResults: row !== null,
		changesPastRuns: true,
		isApplied: (response: DeleteAgenticWorkerResultMutation$data) => response?.deleteAgenticWorkerResult?.deleted === true,
		// Already shown Deleted while in flight, so an applied Delete stays that way until the past runs are read again.
		settledPastPart: (response: DeleteAgenticWorkerResultMutation$data) =>
			response?.deleteAgenticWorkerResult?.deleted ? DELETED_PAST_PART : null,
		onAnswer: (response: DeleteAgenticWorkerResultMutation$data) => {
			const answer = response?.deleteAgenticWorkerResult;
			if (answer?.deleted) {
				listeners.onDeleted();
				return;
			}
			tellActedFirst(listeners.onActedFirst, answer?.result);
		},
	});

export const hideAgentResult = (agentId: string, resultId: string, onHidden: () => void): void =>
	sendResultCommand(agentId, resultId, null, {
		inFlightOf: entry => entry.hidesInFlight,
		key: resultId,
		mutation: HideAgenticWorkerResultMutation,
		// Hiding again changes nothing, so a lost answer can be retried.
		retry: true,
		clearsOtherResults: false,
		// A hidden result is still listed the same way, though its hidden flag is stale until the past runs are next read.
		changesPastRuns: false,
		onAnswer: onHidden,
	});

export const isScopeWorking = (snapshot: AgentRunSnapshot, scope: AgentRunScope): boolean => {
	const scopeKey = toScopeKey(scope);
	if (snapshot.restartAfterStopKeys.has(scopeKey)) {
		return true;
	}
	if (snapshot.pendingStartKeys.has(scopeKey)) {
		return !snapshot.heldStopKeys.has(scopeKey);
	}
	return snapshot.activeRuns.some(run => toScopeKey(run.scope) === scopeKey && !snapshot.stoppingRunIds.has(run.id));
};

const isRowInPageRun = (snapshot: AgentRunSnapshot, row: AgentRow): boolean => {
	const isPageStopHeld =
		snapshot.pendingStartKeys.has(AGENT_RUN_SCOPE.PAGE) && snapshot.heldStopKeys.has(AGENT_RUN_SCOPE.PAGE);
	return (
		!isPageStopHeld &&
		snapshot.activeRuns.some(
			run =>
				run.scope.kind === AGENT_RUN_SCOPE.PAGE && !snapshot.stoppingRunIds.has(run.id) && run.rows.some(coversRow(row))
		)
	);
};

const findMostRecentResult = (snapshot: AgentRunSnapshot, row: AgentRow): AgentResult | null =>
	snapshot.waitingResults.find(waiting => waiting.rows.some(coversRow(row))) ?? null;

const isWholeResultAccepting = (snapshot: AgentRunSnapshot, resultId: string): boolean =>
	snapshot.acceptingKeys.has(toResultCommandKey(resultId, null));

const isWholeResultDeleting = (snapshot: AgentRunSnapshot, resultId: string): boolean =>
	snapshot.deletingKeys.has(toResultCommandKey(resultId, null));

const isRowAccepting = (snapshot: AgentRunSnapshot, result: AgentResult, row: AgentRow): boolean =>
	snapshot.acceptingKeys.has(toResultCommandKey(result.id, row)) || isWholeResultAccepting(snapshot, result.id);

export const getRowState = (snapshot: AgentRunSnapshot, row: AgentRow): AgentRowState => {
	const inPageRun = isRowInPageRun(snapshot, row);
	if (isScopeWorking(snapshot, toRowScope(row)) || inPageRun) {
		return {
			status: AGENT_ROW_STATUS.WORKING,
			result: null,
			reviewedResult: null,
			inPageRun,
			isAccepting: false,
		};
	}
	const mostRecentResult = findMostRecentResult(snapshot, row);
	const isReviewed = mostRecentResult !== null && snapshot.reviewedRows.get(toRowKey(row)) === mostRecentResult.id;
	const result = isReviewed ? null : mostRecentResult;
	return {
		status: result ? AGENT_ROW_STATUS.READY : AGENT_ROW_STATUS.IDLE,
		result,
		reviewedResult: isReviewed ? mostRecentResult : null,
		inPageRun,
		isAccepting: result !== null && isRowAccepting(snapshot, result, row),
	};
};

// A row is marked only while this result is its most recent, so a review never turns a row idle over a newer result
// the person has not seen. A row reviewed again on a later visit leaves its earlier mark for this visit's, so it reads
// Ready until this visit ends too.
export const reviewAgentResult = (agentId: string, result: AgentResult, row: AgentRow | null): void => {
	const entry = entryFor(agentId);
	const reviewedRowKeys = (row === null ? result.rows : [row])
		.filter(candidate => findMostRecentResult(entry.snapshot, candidate)?.id === result.id)
		.map(toRowKey);
	reviewedRowKeys.forEach(rowKey => entry.marksThisVisit.set(rowKey, result.id));
	if (reviewedRowKeys.some(rowKey => entry.earlierVisitMarks.has(rowKey))) {
		entry.earlierVisitMarks = new Map([...entry.earlierVisitMarks].filter(([rowKey]) => !reviewedRowKeys.includes(rowKey)));
		publish(agentId, entry);
	}
	storeReviewedMarks(agentId, entry);
};

export const countWaitingResults = (snapshot: AgentRunSnapshot): number =>
	snapshot.waitingResults.filter(result => !result.hidden).length;

export type AgentPanelState = {
	visibleResults: AgentResult[];
	failedRun: AgentLastRun | null;
	// The results whose whole accept waits for its answer. A row's own accept leaves its result's accept here alone.
	acceptingResultIds: ReadonlySet<string>;
};

const isAgentWorking = (snapshot: AgentRunSnapshot): boolean =>
	snapshot.activeRuns.length > 0 || snapshot.pendingStartKeys.size > 0 || snapshot.restartAfterStopKeys.size > 0;

export const getPanelState = (snapshot: AgentRunSnapshot): AgentPanelState => {
	const {lastRun} = snapshot;
	// A hidden result still waits, so a failed run before it is left to the past runs.
	const showsFailedRun =
		lastRun?.state === AGENT_RUN_STATE.FAILED &&
		!snapshot.lastRunPending &&
		snapshot.waitingResults.length === 0 &&
		!isAgentWorking(snapshot);
	const visibleResults = snapshot.waitingResults.filter(result => !result.hidden && result.rows.length > 0);
	return {
		visibleResults,
		failedRun: showsFailedRun ? lastRun : null,
		acceptingResultIds: new Set(
			visibleResults.filter(result => isWholeResultAccepting(snapshot, result.id)).map(result => result.id)
		),
	};
};

export type AgentPastRunsState = {
	// Null until a read answers. A whole Delete in flight already shows: its Needs Review part reads Deleted, with no who.
	pastRuns: AgentPastRun[] | null;
	counts: AgentPastRunCounts | null;
	// The query the shown answer was read for, else no search and All.
	query: AgentPastRunsQuery;
	canShowMore: boolean;
	isReadingMore: boolean;
	// The results whose whole accept waits for its answer.
	acceptingResultIds: ReadonlySet<string>;
};

const NO_RESULT_IDS: ReadonlySet<string> = new Set();

// Deletes show through the selection, not the store, so a Delete that fails needs nothing undone. Every publish selects
// again, a poll's too, so the runs are walked only while an accept or Delete is in flight.
const getPastRunsState = (snapshot: AgentRunSnapshot): AgentPastRunsState => {
	const {pastRuns} = snapshot;
	const shownResultIds = (): string[] =>
		pastRuns === null ? [] : pastRuns.runs.flatMap(run => run.results.map(part => part.id));
	return {
		pastRuns:
			pastRuns === null || snapshot.deletingKeys.size === 0
				? pastRuns?.runs ?? null
				: settlePastRunParts(pastRuns.runs, resultId => isWholeResultDeleting(snapshot, resultId), DELETED_PAST_PART),
		counts: pastRuns?.counts ?? null,
		query: pastRuns?.query ?? NO_PAST_RUNS_QUERY,
		canShowMore: pastRuns !== null && pastRuns.hasMore && pastRuns.limit < PAST_RUNS_MAX_LIMIT,
		isReadingMore: snapshot.isReadingMorePastRuns,
		acceptingResultIds:
			snapshot.acceptingKeys.size === 0
				? NO_RESULT_IDS
				: new Set(shownResultIds().filter(resultId => isWholeResultAccepting(snapshot, resultId))),
	};
};

export const AGENT_DRAWER = {
	SCHEDULE: 'SCHEDULE',
	PAST_RUNS: 'PAST_RUNS',
} as const;

export type AgentDrawer = (typeof AGENT_DRAWER)[keyof typeof AGENT_DRAWER];

type OpenAgentDrawer = {
	agentId: string;
	drawer: AgentDrawer;
	focusRunId: string | null;
	focusRunRequest: number;
};

type AgentDrawerAnswer = {
	drawer: AgentDrawer | null;
	focusRunId: string | null;
	// Changes each time a run is asked for, the same run again too, and only then.
	focusRunRequest: number;
};

const NO_FOCUS_RUN_REQUEST = 0;

const NO_OPEN_DRAWER: AgentDrawerAnswer = {
	drawer: null,
	focusRunId: null,
	focusRunRequest: NO_FOCUS_RUN_REQUEST,
};

let openDrawer: OpenAgentDrawer | null = null;
let focusRunRequests = NO_FOCUS_RUN_REQUEST;
const openDrawerListeners = new Set<() => void>();

const setOpenDrawer = (next: OpenAgentDrawer | null): void => {
	openDrawer = next;
	unstable_batchedUpdates(() => {
		openDrawerListeners.forEach(listener => listener());
	});
};

// A run asked for starts the past runs afresh, so what an open drawer read for its search and chip is dropped in the
// same publish: the drawer never shows it, nor brings the run into view from it.
export const openAgentDrawer = (agentId: string, drawer: AgentDrawer, focusRunId: string | null = null): void => {
	if (focusRunId !== null) {
		focusRunRequests += 1;
	}
	const entry = entries.get(agentId);
	unstable_batchedUpdates(() => {
		if (focusRunId !== null && entry !== undefined && entry.pastRunsWatchers > 0) {
			forgetPastRuns(agentId, entry);
		}
		setOpenDrawer({
			agentId,
			drawer,
			focusRunId,
			focusRunRequest: focusRunRequests,
		});
	});
};

export const closeAgentDrawer = (agentId: string, drawer: AgentDrawer): void => {
	if (openDrawer !== null && openDrawer.agentId === agentId && openDrawer.drawer === drawer) {
		setOpenDrawer(null);
	}
};

const answerOpenDrawer = (agentId: string): AgentDrawerAnswer =>
	openDrawer !== null && openDrawer.agentId === agentId
		? {
				drawer: openDrawer.drawer,
				focusRunId: openDrawer.focusRunId,
				focusRunRequest: openDrawer.focusRunRequest,
		  }
		: NO_OPEN_DRAWER;

export const useOpenAgentDrawer = (agentId: string): AgentDrawerAnswer => {
	const answer = answerOpenDrawer(agentId);
	const [, rerender] = useReducer((renders: number) => renders + 1, 0);
	const renderedRef = useRef({agentId, answer});

	useLayoutEffect(() => {
		renderedRef.current = {agentId, answer};
	});

	useEffect(() => {
		const rerenderOnChange = (): void => {
			if (!isEqual(renderedRef.current.answer, answerOpenDrawer(agentId))) {
				rerender();
			}
		};
		rerenderOnChange();
		openDrawerListeners.add(rerenderOnChange);
		return () => {
			openDrawerListeners.delete(rerenderOnChange);
		};
	}, [agentId]);

	return answer;
};

export const clearAgentRunStore = (): void => {
	openDrawer = null;
	focusRunRequests = NO_FOCUS_RUN_REQUEST;
	openDrawerListeners.clear();
	entries.forEach(entry => {
		cancelPoll(entry);
		cancelPastRunsRetry(entry);
		stopFollowingAgentSchedule(entry);
		entry.listeners.clear();
		entry.finishListeners.clear();
		forgetWatchedRuns(entry);
	});
	entries.clear();
	document.removeEventListener(VISIBILITY_CHANGE_EVENT, readSubscribedAgentsOnReturn);
};

type AgentRunCommands = {
	startRun: () => void;
	stopRun: () => void;
};

// Each answers whether it ran. accept and deleteResult run only while the row still shows the result they rendered with.
type AgentRowResultCommands = {
	review: () => void;
	accept: () => boolean;
	runAgain: () => boolean;
	deleteResult: () => boolean;
};

export type AgentRowControls = AgentRowState & AgentRunCommands & AgentRowResultCommands;

type AgentPageState = {
	isRunning: boolean;
	waitingCount: number;
};

export type AgentPageControls = AgentPageState & AgentRunCommands;

export type AgentStatusLineState = {
	firstReadAnswered: boolean;
	isRunning: boolean;
	waitingCount: number;
	lastRunFinishedAt: string | null;
	scheduleOff: boolean | null;
};

// The waiting count in words, as the button, the Past Runs item and the status line each say it.
export const formatWaitingCount = ({formatMessage}: IntlShape, declaration: AgentDeclaration, count: number): string =>
	formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.BADGE_WAITING},
		{
			count,
			resultSingular: formatMessage(declaration.resultNoun.singular),
			resultPlural: formatMessage(declaration.resultNoun.plural),
		}
	);

// Why a run failed, as the failed card and the past runs both say it: the agent's own words, else the components'.
export const describeFailureReason = (
	declaration: AgentDeclaration,
	run: Pick<AgentLastRun, 'stateReason' | 'detail'>,
	intl: IntlShape
): string => {
	const agentReason = declaration.failureReason(
		{
			state: AGENT_RUN_STATE.FAILED,
			stateReason: run.stateReason,
			results: [],
			detail: run.detail,
		},
		intl
	);
	return (
		agentReason ??
		(run.stateReason === AGENT_RUN_STATE_REASON.TIMED_OUT
			? intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_REASON_TIMED_OUT}, {minutes: RUN_TIME_LIMIT_MINUTES})
			: intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_REASON_UNKNOWN}))
	);
};

const showToast = (message: string): void =>
	createToast({
		duration: TOAST_DURATION_MILLISECONDS,
		message,
	});

const useRunStoppedToast = (): (() => void) => {
	const {formatMessage} = useIntl();
	return () => showToast(formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_RUN_STOPPED}));
};

// The components never know a record's name, so the agent names it in its description of the new result. Only the row's
// own rows are described, as rows without a readable project give no record and would shift the positions.
const runAgainRecordName = (declaration: AgentDeclaration, result: AgentResult, row: AgentRow, intl: IntlShape): string => {
	const rowResult: AgentResult = {...result, rows: result.rows.filter(coversRow(row))};
	const record: AgentRecord | undefined = declaration.describeResult(rowResult, intl).records[0];
	return record?.name || intl.formatMessage(declaration.recordNoun);
};

// Null when no result of the run includes the row by the time the run is told, so no toast says a result is ready that
// is not there.
const describeRunFinished = (
	declaration: AgentDeclaration,
	outcome: AgentRunOutcome,
	scope: AgentRunScope,
	intl: IntlShape
): string | null => {
	if (scope.kind === AGENT_RUN_SCOPE.PAGE) {
		return declaration.summaryToast(outcome, intl);
	}
	const result = outcome.results.find(candidate => candidate.rows.some(coversRow(scope)));
	if (result === undefined) {
		return null;
	}
	return intl.formatMessage(declaration.runAgainToast, {
		record: runAgainRecordName(declaration, result, scope, intl),
	});
};

// One toast per tab only while a single control per agent is mounted: each mounted control subscribes and toasts.
export const useRunFinishedToasts = (declaration: AgentDeclaration): void => {
	const intl = useIntl();
	useEffect(
		() =>
			subscribeToRunFinished(declaration.id, (outcome, scope) => {
				const message = describeRunFinished(declaration, outcome, scope, intl);
				if (message !== null) {
					showToast(message);
				}
			}),
		[declaration, intl]
	);
};

type ResultToasts = {
	showDeleted: () => void;
	showHidden: () => void;
	showRefused: (reason: string) => void;
	showActedFirst: (actedByName: string | null, state: AgentResultState | null) => void;
};

const upperCaseFirstLetter = (text: string, locale: string): string =>
	`${text.charAt(0).toLocaleUpperCase(locale)}${text.slice(1)}`;

export const useResultToasts = (declaration: AgentDeclaration): ResultToasts => {
	const {formatMessage, locale} = useIntl();
	const resultNoun = (): string => formatMessage(declaration.resultNoun.singular);
	return {
		showDeleted: () =>
			showToast(
				formatMessage(
					{id: AGENTIC_WORKERS_MESSAGE.TOAST_RESULT_DELETED},
					{result: upperCaseFirstLetter(resultNoun(), locale)}
				)
			),
		showHidden: () => showToast(formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_RESULT_HIDDEN})),
		// The reason is already in the agent's own words.
		showRefused: reason => showToast(reason),
		showActedFirst: (actedByName, state) =>
			showToast(
				formatMessage(
					{
						id:
							state === AGENT_RESULT_STATE.DELETED
								? AGENTIC_WORKERS_MESSAGE.TOAST_ALREADY_DELETED
								: AGENTIC_WORKERS_MESSAGE.TOAST_ALREADY_ACTED,
					},
					{
						person: actedByName ?? formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_SOMEONE}),
						result: resultNoun(),
					}
				)
			),
	};
};

const useAgentRunCommands = (agentId: string, scope: AgentRunScope): AgentRunCommands => {
	const showRunStoppedToast = useRunStoppedToast();
	return {
		startRun: () => startAgentRun(agentId, scope),
		stopRun: () => stopAgentRun(agentId, scope, showRunStoppedToast),
	};
};

// Selects from the latest snapshot as it renders, and re-renders only when a store change changes the selection, so a
// change to one row leaves every other row's control alone.
const useAgentRunSelection = <Selection>(
	agentId: string,
	select: (snapshot: AgentRunSnapshot) => Selection,
	isSameSelection: (rendered: Selection, latest: Selection) => boolean
): Selection => {
	const selection = select(getAgentRunSnapshot(agentId));
	const [, rerender] = useReducer((renders: number) => renders + 1, 0);
	const renderedRef = useRef({selection, select, isSameSelection});

	// Updated as the render commits, so a store change that lands before the effects run is compared with what shows.
	useLayoutEffect(() => {
		renderedRef.current = {selection, select, isSameSelection};
	});

	useEffect(() => {
		const rerenderOnChange = (): void => {
			const rendered = renderedRef.current;
			if (!rendered.isSameSelection(rendered.selection, rendered.select(getAgentRunSnapshot(agentId)))) {
				rerender();
			}
		};
		rerenderOnChange();
		return subscribeToAgentRun(agentId, rerenderOnChange);
	}, [agentId]);

	return selection;
};

const isSameRowState = (rendered: AgentRowState, latest: AgentRowState): boolean =>
	rendered.status === latest.status &&
	rendered.inPageRun === latest.inPageRun &&
	rendered.isAccepting === latest.isAccepting &&
	isEqual(rendered.result, latest.result) &&
	isEqual(rendered.reviewedResult, latest.reviewedResult);

const getPageState = (snapshot: AgentRunSnapshot): AgentPageState => ({
	isRunning: isScopeWorking(snapshot, PAGE_RUN_SCOPE),
	waitingCount: countWaitingResults(snapshot),
});

// The last run is the one the store has read. lastRunPending is left out: a refused start or any stop raises it too,
// when nothing new ran.
const getStatusLineState = (snapshot: AgentRunSnapshot): AgentStatusLineState => ({
	...getPageState(snapshot),
	firstReadAnswered: snapshot.firstReadAnswered,
	lastRunFinishedAt: snapshot.lastRun?.finishedAt ?? null,
	scheduleOff: snapshot.scheduleOff,
});

export const useAgentRowState = (declaration: AgentDeclaration, row: AgentRow): AgentRowControls => {
	const rowState = useAgentRunSelection(declaration.id, snapshot => getRowState(snapshot, row), isSameRowState);
	const showRunStoppedToast = useRunStoppedToast();
	const {showDeleted, showRefused, showActedFirst} = useResultToasts(declaration);
	const rowScope = toRowScope(row);
	// A menu runs its command once it has closed, by when the row may show another result or none.
	const actOnRowResult = (command: (result: AgentResult) => void) => (): boolean => {
		const shownResult = rowState.result;
		if (!shownResult || getRowState(getAgentRunSnapshot(declaration.id), row).result?.id !== shownResult.id) {
			return false;
		}
		command(shownResult);
		return true;
	};
	const resultToReview = rowState.result ?? rowState.reviewedResult;
	return {
		...rowState,
		startRun: () => startAgentRun(declaration.id, rowScope),
		// A row in a page run stops that run too, and the two stops answer with one toast.
		stopRun: () => {
			const snapshot = getAgentRunSnapshot(declaration.id);
			const showToastOnce = once(showRunStoppedToast);
			if (isScopeWorking(snapshot, rowScope)) {
				stopAgentRun(declaration.id, rowScope, showToastOnce);
			}
			if (isRowInPageRun(snapshot, row)) {
				stopAgentRun(declaration.id, PAGE_RUN_SCOPE, showToastOnce);
			}
		},
		review: () => {
			if (resultToReview) {
				reviewAgentResult(declaration.id, resultToReview, row);
				declaration.review(resultToReview);
			}
		},
		accept: actOnRowResult(result =>
			acceptAgentResult(declaration.id, result.id, row, {
				onRefused: showRefused,
				onActedFirst: showActedFirst,
			})
		),
		// A fresh run for the row, whatever result it shows by now.
		runAgain: () => {
			startAgentRun(declaration.id, rowScope, {announceRunAgain: true});
			return true;
		},
		deleteResult: actOnRowResult(result =>
			deleteAgentResult(declaration.id, result.id, row, {
				onDeleted: showDeleted,
				onActedFirst: showActedFirst,
			})
		),
	};
};

export const useAgentPageState = (declaration: AgentDeclaration): AgentPageControls => {
	const pageState = useAgentRunSelection(declaration.id, getPageState, isEqual);
	const commands = useAgentRunCommands(declaration.id, PAGE_RUN_SCOPE);
	return {
		...pageState,
		...commands,
	};
};

export const useAgentStatusLineState = (declaration: AgentDeclaration): AgentStatusLineState => {
	const statusLineState = useAgentRunSelection(declaration.id, getStatusLineState, isEqual);
	useEffect(
		() => watchAgentSchedule(declaration.id, declaration.schedulingTarget),
		[declaration.id, declaration.schedulingTarget]
	);
	return statusLineState;
};

type AgentResultCommands = {
	review: (result: AgentResult) => void;
	accept: (result: AgentResult) => void;
	deleteResult: (result: AgentResult) => void;
};

// The panel's and the past runs' commands on a whole result, so an accept or Delete pressed in both is sent once.
const useResultCommands = (declaration: AgentDeclaration): AgentResultCommands => {
	const {showDeleted, showRefused, showActedFirst} = useResultToasts(declaration);
	return {
		review: result => {
			reviewAgentResult(declaration.id, result, null);
			declaration.review(result);
		},
		accept: result =>
			acceptAgentResult(declaration.id, result.id, null, {
				onRefused: showRefused,
				onActedFirst: showActedFirst,
			}),
		deleteResult: result =>
			deleteAgentResult(declaration.id, result.id, null, {
				onDeleted: showDeleted,
				onActedFirst: showActedFirst,
			}),
	};
};

export type AgentPanelControls = AgentPanelState &
	AgentResultCommands & {
		hideResult: (result: AgentResult) => void;
		retry: () => void;
	};

export const useAgentPanelState = (declaration: AgentDeclaration): AgentPanelControls => {
	const panelState = useAgentRunSelection(declaration.id, getPanelState, isEqual);
	const resultCommands = useResultCommands(declaration);
	const {showHidden} = useResultToasts(declaration);
	const {failedRun} = panelState;
	return {
		...panelState,
		...resultCommands,
		hideResult: result => hideAgentResult(declaration.id, result.id, showHidden),
		retry: () => {
			if (failedRun) {
				startAgentRun(declaration.id, failedRun.scope);
			}
		},
	};
};

export type AgentPastRunsControls = AgentPastRunsState &
	AgentResultCommands & {
		setQuery: (query: AgentPastRunsQuery) => void;
		showMore: () => void;
	};

// Subscribed to the runs as well as watching the past runs, so a run ending or a return to the tab reads them again.
export const useAgentPastRunsState = (declaration: AgentDeclaration): AgentPastRunsControls => {
	const pastRunsState = useAgentRunSelection(declaration.id, getPastRunsState, isEqual);
	const resultCommands = useResultCommands(declaration);
	useEffect(() => watchAgentPastRuns(declaration.id), [declaration.id]);
	return {
		...pastRunsState,
		...resultCommands,
		setQuery: query => setAgentPastRunsQuery(declaration.id, query),
		showMore: () => showMoreAgentPastRuns(declaration.id),
	};
};
